import React, { useState, useEffect, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { MapPin, Navigation, Phone, Mail } from 'lucide-react';

const GoogleMap = ({ 
  address = 'Mumbai, Maharashtra',
  apiKey = null,
  height = '400px',
  width = '100%',
  zoom = 15,
  className = '',
  showInfoWindow = true,
  showDirections = true,
  showContactInfo = false,
  contactInfo = {},
  markerPosition = null,
  onMapClick = null,
  draggable = true
}) => {
  const [mapsApiKey, setMapsApiKey] = useState(apiKey);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [coordinates, setCoordinates] = useState(null);
  const mapRef = useRef(null);
  const loaderRef = useRef(null);
  const googleRef = useRef(null);
  const geocodeTimerRef = useRef(null);

  // Fetch Google Maps API key from backend if not provided
  useEffect(() => {
    const fetchMapsApiKey = async () => {
      if (!apiKey) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/maps/config`);
          const data = await response.json();
          if (data.success && data.data.enabled && data.data.apiKey) {
            setMapsApiKey(data.data.apiKey);
          }
        } catch (error) {
          console.error('Failed to fetch Google Maps API key:', error);
        }
      }
    };

    fetchMapsApiKey();
  }, [apiKey]);

  // Load Google API once
  useEffect(() => {
    if (!mapsApiKey || googleRef.current) return;
    const load = async () => {
      try {
        loaderRef.current = new Loader({ apiKey: mapsApiKey, version: 'weekly', libraries: ['places'] });
        const google = await loaderRef.current.load();
        googleRef.current = google;
      } catch (e) {
        console.error('Google loader error:', e);
        setError('Failed to load Google Maps API');
      }
    };
    load();
  }, [mapsApiKey]);

  // Initialize map once
  useEffect(() => {
    const init = async () => {
      if (!googleRef.current || !mapRef.current || map) return;
      setIsLoading(true);
      try {
        const { Geocoder } = await googleRef.current.maps.importLibrary('geocoding');
        const { Map } = await googleRef.current.maps.importLibrary('maps');
        const geocoder = new Geocoder();
        const result = await geocoder.geocode({ address });
        if (!result.results.length) {
          // Graceful fallback: center to a default and continue without error
          const fallback = { lat: 19.0760, lng: 72.8777 }; // Mumbai
          setCoordinates(fallback);
          const { Map } = await googleRef.current.maps.importLibrary('maps');
          const mapInstance = new Map(mapRef.current, { center: fallback, zoom, mapTypeControl: true, streetViewControl: true, fullscreenControl: true, zoomControl: true });
          const markerInstance = new googleRef.current.maps.Marker({ position: fallback, map: mapInstance, title: 'Location not found' });
          setMap(mapInstance);
          setMarker(markerInstance);
          setIsLoading(false);
          return;
        }
        const location = result.results[0].geometry.location;
        setCoordinates({ lat: location.lat(), lng: location.lng() });
        const mapInstance = new Map(mapRef.current, {
          center: location,
          zoom,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }]
        });
        const markerInstance = new googleRef.current.maps.Marker({ 
          position: location, 
          map: mapInstance, 
          title: address, 
          draggable: draggable 
        });
        
        if (onMapClick) {
          mapInstance.addListener('click', (e) => {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            markerInstance.setPosition({ lat, lng });
            mapInstance.setCenter({ lat, lng });
            onMapClick({ lat, lng });
          });
          
          // Also handle marker drag events
          markerInstance.addListener('dragend', (e) => {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            onMapClick({ lat, lng });
          });
        }
        if (showInfoWindow) {
          const infoWindowInstance = new googleRef.current.maps.InfoWindow({
            content: `
              <div style="padding: 10px; max-width: 300px;">
                <h3 style="margin: 0 0 10px 0; color: #333; font-weight: bold;">Our Location</h3>
                <p style="margin: 0 0 8px 0; color: #666;">${address}</p>
                ${showContactInfo && contactInfo.phone ? `<p style="margin: 0 0 8px 0; color: #666;">📞 ${contactInfo.phone}</p>` : ''}
                ${showContactInfo && contactInfo.email ? `<p style="margin: 0 0 8px 0; color: #666;">✉️ ${contactInfo.email}</p>` : ''}
                ${showDirections ? `<a href="https://maps.google.com/maps?daddr=${encodeURIComponent(address)}" target="_blank" style="color: #3B82F6; text-decoration: none; font-weight: 500;">Get Directions</a>` : ''}
              </div>`
          });
          markerInstance.addListener('click', () => infoWindowInstance.open(mapInstance, markerInstance));
          setInfoWindow(infoWindowInstance);
        }
        setMap(mapInstance);
        setMarker(markerInstance);
      } catch (e) {
        console.error('Map init error:', e);
        // Non-blocking fallback
        const fallback = { lat: 19.0760, lng: 72.8777 };
        try {
          const { Map } = await googleRef.current.maps.importLibrary('maps');
          const mapInstance = new Map(mapRef.current, { center: fallback, zoom, mapTypeControl: true, streetViewControl: true, fullscreenControl: true, zoomControl: true });
          const markerInstance = new googleRef.current.maps.Marker({ 
            position: fallback, 
            map: mapInstance, 
            title: 'Default location',
            draggable: draggable 
          });
          setMap(mapInstance);
          setMarker(markerInstance);
          setCoordinates(fallback);
          setError(null);
        } catch (inner) {
          setError(e.message);
        }
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [address, map, showInfoWindow, showDirections, showContactInfo, contactInfo, zoom]);

  // External marker position updates
  useEffect(() => {
    if (!map || !marker || !markerPosition) return;
    marker.setPosition(markerPosition);
    map.setCenter(markerPosition);
  }, [markerPosition, map, marker]);

  // Debounced address updates: update marker/center without recreating map
  useEffect(() => {
    if (!map || !googleRef.current) return;
    if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    geocodeTimerRef.current = setTimeout(async () => {
      try {
        setIsUpdating(true);
        const { Geocoder } = await googleRef.current.maps.importLibrary('geocoding');
        const geocoder = new Geocoder();
        const result = await geocoder.geocode({ address });
        if (!result.results.length) throw new Error('Address not found');
        const location = result.results[0].geometry.location;
        setCoordinates({ lat: location.lat(), lng: location.lng() });
        if (marker) {
          marker.setPosition(location);
        }
        map.setCenter(location);
      } catch (e) {
        console.error('Geocode update error:', e);
        setError(e.message);
      } finally {
        setIsUpdating(false);
      }
    }, 400);
    return () => {
      if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    };
  }, [address, map, marker]);

  // Handle directions
  const handleDirections = () => {
    if (coordinates) {
      const directionsUrl = `https://maps.google.com/maps?daddr=${coordinates.lat},${coordinates.lng}`;
      window.open(directionsUrl, '_blank');
    }
  };

  // Handle phone call
  const handlePhoneCall = () => {
    if (contactInfo.phone) {
      window.location.href = `tel:${contactInfo.phone}`;
    }
  };

  // Handle email
  const handleEmail = () => {
    if (contactInfo.email) {
      window.location.href = `mailto:${contactInfo.email}`;
    }
  };

  if (error) {
    return (
      <div className={`bg-gray-100 dark:bg-gray-800 rounded-lg p-8 text-center ${className}`} style={{ height, width }}>
        <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Location</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{address}</p>
        <p className="text-sm text-red-500">Unable to load map: {error}</p>
        {showDirections && (
          <button
            onClick={handleDirections}
            className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Navigation className="w-4 h-4 mr-2" />
            Get Directions
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ height, width }}>
      {(isLoading || isUpdating) && (
        <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400">{isLoading ? 'Loading map...' : 'Updating location...'}</p>
          </div>
        </div>
      )}
      
      <div ref={mapRef} className="w-full h-full rounded-lg" />
      
      {/* Contact Actions */}
      {showContactInfo && (contactInfo.phone || contactInfo.email) && (
        <div className="absolute bottom-4 right-4 flex flex-col space-y-2">
          {contactInfo.phone && (
            <button
              onClick={handlePhoneCall}
              className="bg-white dark:bg-gray-800 p-2 rounded-full shadow-lg hover:shadow-xl transition-shadow"
              title="Call us"
            >
              <Phone className="w-5 h-5 text-blue-600" />
            </button>
          )}
          {contactInfo.email && (
            <button
              onClick={handleEmail}
              className="bg-white dark:bg-gray-800 p-2 rounded-full shadow-lg hover:shadow-xl transition-shadow"
              title="Email us"
            >
              <Mail className="w-5 h-5 text-blue-600" />
            </button>
          )}
          {showDirections && (
            <button
              onClick={handleDirections}
              className="bg-white dark:bg-gray-800 p-2 rounded-full shadow-lg hover:shadow-xl transition-shadow"
              title="Get directions"
            >
              <Navigation className="w-5 h-5 text-blue-600" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default GoogleMap;
